import { Fragment, useEffect, useState } from 'react';

import { gql, useMutation } from '@apollo/client';
import { AlertTitle } from '@material-ui/lab';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubGrid,
  DevHubModal,
  DevHubStep,
  DevHubStepContent,
  DevHubStepLabel,
  DevHubStepper,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

const CREATE_PLUGIN_MUTATION = gql`
  mutation CreateExtension($input: CreateExtensionInput!) {
    devhub {
      admin {
        createExtension(input: $input) {
          success
          message
          extension {
            id
            name
            owner
            extensionType
            distributionList
            slackChannel
            notes
            securityCredentialsUrl
          }
        }
      }
    }
  }
`;

/**
 * This component is used to manually create a new DevHub Extension
 * @constructor
 */
const AddExtensionCard = () => {
  const steps = getSteps();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [validationHasError, setValidationHasError] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [reviewData, setReviewData] = useState({});
  const [isPluginSelected, setIsPluginSelected] = useState(false);
  const [isTemplateSelected, setIsTemplateSelected] = useState(false);

  const [formData, setFormData] = useState({
    extensionType: '',
    extensionName: '',
    displayName: '',
    description: '',
    owner: '',
    packageScope: '',
    repoOwner: '',
    repoLocation: '',
    slackChannel: '',
    distributionList: '',
    securityCredentialsUrl: '',
    notes: '',
  });

  useEffect(() => {
    setReviewData({
      'Extension Type': formData.extensionType,
      'Extension Name': formData.extensionName,
      'Display Name': formData.displayName,
      Description: formData.description,
      Owner: formData.owner,
      'Package Scope': formData.packageScope,
      'Repo Owner': formData.repoOwner,
      'Repo Location': formData.repoLocation,
      'Slack Channel': formData.slackChannel,
      'Distribution List': formData.distributionList,
      'Security Credentials Url': formData.securityCredentialsUrl,
      Notes: formData.notes,
    });
  }, [formData]);

  const handleReset = () => {
    setActiveStep(0);
    setValidationHasError(false);
    setIsPluginSelected(false);
    setIsTemplateSelected(false);
    setFormData({
      extensionType: '',
      extensionName: '',
      displayName: '',
      description: '',
      owner: '',
      packageScope: '',
      repoOwner: '',
      repoLocation: '',
      slackChannel: '',
      distributionList: '',
      securityCredentialsUrl: '',
      notes: '',
    });
  };

  const handleOpen = () => {
    setModalIsOpen(true);
  };

  const handleClose = () => {
    setModalIsOpen(false);
    handleReset();
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return (
          formData.extensionType !== '' &&
          formData.extensionName !== '' &&
          formData.displayName !== '' &&
          formData.description !== '' &&
          formData.owner !== '' &&
          formData.packageScope !== ''
        );
      case 1:
        return (
          formData.repoOwner !== '' &&
          formData.repoLocation !== '' &&
          formData.slackChannel !== '' &&
          formData.distributionList !== '' &&
          formData.securityCredentialsUrl !== '' &&
          formData.notes !== ''
        );
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
      setValidationHasError(false);
    } else {
      setValidationHasError(true);
    }
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
    setValidationHasError(false);
  };

  const [createPlugin] = useMutation(CREATE_PLUGIN_MUTATION, {
    onCompleted: data => {
      if (data.devhub.admin.createExtension.success) {
        setAlertMessage('Extension created successfully!');
        setAlertOpen(true);
      }
    },
  });

  const handleSubmit = async () => {
    const inputData = {
      name: formData.extensionName,
      distributionList: formData.distributionList,
      extensionType: formData.extensionType,
      notes: formData.notes,
      owner: formData.owner,
      securityCredentialsUrl: formData.securityCredentialsUrl,
      slackChannel: formData.slackChannel,
    };

    await createPlugin({ variables: { input: inputData } });
  };
  const handleChange = (field: string, value: string) => {
    setFormData(prevData => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleSelectPlugin = () => {
    setIsPluginSelected(true);
    setIsTemplateSelected(false);
    setFormData({ ...formData, extensionType: 'Plugin' });
  };

  const handleSelectTemplate = () => {
    setIsPluginSelected(false);
    setIsTemplateSelected(true);
    setFormData({ ...formData, extensionType: 'Template' });
  };

  function getSteps() {
    return ['Extension Details', 'Additional Info', 'Review & Submit'];
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DevHubBox>
            <Fragment>
              <DevHubTypography>Select Extension Type</DevHubTypography>
              <DevHubGrid container padding="xsmall">
                <DevHubGrid item>
                  <DevHubButton
                    value="Plugin"
                    onClick={handleSelectPlugin}
                    variant={isPluginSelected ? 'contained' : 'outlined'}
                  >
                    Plugin
                  </DevHubButton>
                </DevHubGrid>
                <DevHubGrid item>
                  <DevHubButton
                    value="Template"
                    onClick={handleSelectTemplate}
                    variant={isTemplateSelected ? 'contained' : 'outlined'}
                  >
                    Template
                  </DevHubButton>
                </DevHubGrid>
              </DevHubGrid>
            </Fragment>
            <DevHubTextField
              label="Name"
              value={formData.extensionName}
              onChange={(e: { target: { value: string } }) =>
                handleChange('extensionName', e.target.value)
              }
              fullWidth
              helperText='Unique name of the Extension prefixed with "plugin-" if it is a plugin'
            />
            <DevHubTextField
              label="Display Name"
              value={formData.displayName}
              onChange={(e: { target: { value: string } }) =>
                handleChange('displayName', e.target.value)
              }
              fullWidth
              helperText="The display name of the extension"
            />
            <DevHubTextField
              label="Description"
              value={formData.description}
              onChange={(e: { target: { value: string } }) =>
                handleChange('description', e.target.value)
              }
              fullWidth
              helperText="A short description of the extension"
            />
            <DevHubTextField
              label="Owner"
              value={formData.owner}
              onChange={(e: { target: { value: string } }) =>
                handleChange('owner', e.target.value)
              }
              fullWidth
              helperText="The owner of the extension."
            />
            <DevHubTextField
              label="Package Scope"
              value={formData.packageScope}
              onChange={(e: { target: { value: string } }) =>
                handleChange('packageScope', e.target.value)
              }
              fullWidth
              helperText="The scope of the NPM package. This is typically your organization's name without any special characters (e.g., devx-discover = devxdiscover)."
            />
          </DevHubBox>
        );
      case 1:
        return (
          <DevHubBox>
            <DevHubTextField
              label="Owner"
              value={formData.repoOwner}
              onChange={(e: { target: { value: string } }) =>
                handleChange('repoOwner', e.target.value)
              }
              fullWidth
              helperText="The organization, user or project that this repo will belong to"
            />
            <DevHubTextField
              label="Repository"
              value={formData.repoLocation}
              onChange={(e: { target: { value: string } }) =>
                handleChange('repoLocation', e.target.value)
              }
              fullWidth
              helperText="The name of the repository."
            />
            <DevHubTextField
              label="Slack Channel"
              value={formData.slackChannel}
              onChange={(e: { target: { value: string } }) =>
                handleChange('slackChannel', e.target.value)
              }
              fullWidth
              helperText="The Slack channel where the extension will be discussed."
            />
            <DevHubTextField
              label="Distribution List"
              value={formData.distributionList}
              onChange={(e: { target: { value: string } }) =>
                handleChange('distributionList', e.target.value)
              }
              fullWidth
              helperText="The distribution list for the extension."
            />
            <DevHubTextField
              label="Security Credentials Url"
              value={formData.securityCredentialsUrl}
              onChange={(e: { target: { value: string } }) =>
                handleChange('securityCredentialsUrl', e.target.value)
              }
              fullWidth
              helperText="The url for the security credentials."
            />
            <DevHubTextField
              label="Notes"
              value={formData.notes}
              onChange={(e: { target: { value: string } }) =>
                handleChange('notes', e.target.value)
              }
              fullWidth
              helperText="Any additional notes about the extension."
            />
          </DevHubBox>
        );
      case 2:
        return (
          <DevHubCard
            density="compact"
            title="Extension Details"
            body={
              <DevHubGrid container margin="xsmall">
                {Object.entries(reviewData).map(([key, value]) => (
                  <DevHubGrid key={key} item xs={12}>
                    <DevHubTypography>
                      <strong>{key}:</strong> {String(value)}
                    </DevHubTypography>
                  </DevHubGrid>
                ))}
              </DevHubGrid>
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <DevHubCard
        density="compact"
        title="Create an Extension"
        actionItem={
          <DevHubGrid container margin="xsmall">
            <DevHubButton
              buttonColor="primary"
              variant="outlined"
              onClick={handleOpen}
            >
              Open
            </DevHubButton>
          </DevHubGrid>
        }
        subTitle="Manually create an extension"
      />
      <DevHubModal open={modalIsOpen} onClose={handleClose}>
        <Fragment>
          <DevHubTypography variant="h3">Create an Extension</DevHubTypography>
          <DevHubStepper activeStep={activeStep} orientation="vertical">
            {steps.map((label, index) => (
              <DevHubStep key={label}>
                <DevHubStepLabel>
                  <DevHubTypography>{label}</DevHubTypography>
                </DevHubStepLabel>
                <DevHubStepContent>
                  <DevHubTypography>{getStepContent(index)}</DevHubTypography>
                  <DevHubBox>
                    {validationHasError && activeStep > 0 && (
                      <DevHubTypography variant="subtitle1" color="error">
                        Please complete the form before proceeding.
                      </DevHubTypography>
                    )}
                    <DevHubGrid container padding="small">
                      <DevHubGrid item>
                        <DevHubButton
                          onClick={handleReset}
                          disabled={activeStep === 0}
                        >
                          Reset
                        </DevHubButton>
                      </DevHubGrid>
                      <DevHubGrid item>
                        <DevHubButton
                          disabled={activeStep === 0}
                          onClick={handleBack}
                        >
                          Back
                        </DevHubButton>
                      </DevHubGrid>

                      {activeStep !== steps.length - 1 && (
                        <DevHubGrid item>
                          <DevHubButton
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={!isStepValid(activeStep)}
                          >
                            Next
                          </DevHubButton>
                        </DevHubGrid>
                      )}
                      {activeStep === steps.length - 1 && (
                        <DevHubGrid item>
                          <DevHubButton
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                          >
                            Submit
                          </DevHubButton>
                        </DevHubGrid>
                      )}
                    </DevHubGrid>
                  </DevHubBox>
                </DevHubStepContent>
              </DevHubStep>
            ))}
          </DevHubStepper>
          <DevHubBox mt={2}>
            {alertOpen && (
              <DevHubAlert
                onClose={() => setAlertOpen(false)}
                severity="success"
                variant="filled"
              >
                <AlertTitle>Success</AlertTitle>
                {alertMessage}
              </DevHubAlert>
            )}
          </DevHubBox>
        </Fragment>
      </DevHubModal>
    </Fragment>
  );
};

export default AddExtensionCard;
